<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DealStatusBadge from './DealStatusBadge.vue';

const props = defineProps({
  mapping: {
    type: Object,
    required: true,
  },
  showActions: {
    type: Boolean,
    default: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'markWon', 'markLost', 'reopen']);

const { t } = useI18n();

const hasDealValue = computed(() => {
  return props.mapping.dealValue && props.mapping.dealValue > 0;
});

const currencySymbols = {
  USD: '$',
  EUR: '€',
  BRL: 'R$',
};

const formattedValue = computed(() => {
  if (!hasDealValue.value) return null;
  const currency = props.mapping.dealCurrency || 'BRL';
  const symbol = currencySymbols[currency] || 'R$';
  return `${symbol} ${props.mapping.dealValue.toFixed(2)}`;
});

const formattedWeightedValue = computed(() => {
  if (!props.mapping.weightedValue) return null;
  const currency = props.mapping.dealCurrency || 'BRL';
  const symbol = currencySymbols[currency] || 'R$';
  return `${symbol} ${props.mapping.weightedValue.toFixed(2)}`;
});

const daysUntilClose = computed(() => {
  return props.mapping.daysUntilClose;
});

const isOverdue = computed(() => {
  return props.mapping.isOverdue;
});

const dealStatus = computed(() => {
  return props.mapping.dealStatus || 'deal_open';
});

const isOpen = computed(() => dealStatus.value === 'deal_open');
const isWon = computed(() => dealStatus.value === 'deal_won');
const isLost = computed(() => dealStatus.value === 'deal_lost');
</script>

<template>
  <div class="deal-card" :class="{ 'deal-card--compact': compact }">
    <!-- Header with value and status -->
    <div class="deal-card__header">
      <div v-if="hasDealValue" class="deal-card__value">
        <span class="deal-card__value-amount">{{ formattedValue }}</span>
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span v-if="mapping.probability" class="deal-card__probability">
          {{ mapping.probability }}%
        </span>
      </div>
      <DealStatusBadge :status="dealStatus" size="sm" />
    </div>

    <!-- Details -->
    <div v-if="!compact" class="deal-card__details">
      <!-- Weighted Value -->
      <div v-if="formattedWeightedValue" class="deal-card__detail">
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="deal-card__detail-label">
          {{ t('CRM.DEAL.WEIGHTED_VALUE') }}:
        </span>
        <span class="deal-card__detail-value deal-card__detail-value--weighted">
          {{ formattedWeightedValue }}
        </span>
      </div>

      <!-- Expected Close Date -->
      <div v-if="mapping.expectedCloseDate" class="deal-card__detail">
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="deal-card__detail-label">
          {{ t('CRM.DEAL.EXPECTED_CLOSE') }}:
        </span>
        <span
          class="deal-card__detail-value"
          :class="{ 'deal-card__detail-value--overdue': isOverdue }"
        >
          {{ mapping.expectedCloseDate }}
          <span v-if="daysUntilClose !== null && isOpen && daysUntilClose >= 0">
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            ({{ daysUntilClose }}d)
          </span>
          <span v-if="daysUntilClose !== null && isOpen && daysUntilClose < 0">
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            ({{ Math.abs(daysUntilClose) }}d atrasado)
          </span>
        </span>
      </div>

      <!-- Won/Lost info -->
      <div v-if="isWon && mapping.wonAt" class="deal-card__detail">
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="deal-card__detail-label">{{ t('CRM.DEAL.WON_AT') }}:</span>
        <span class="deal-card__detail-value">{{ mapping.wonAt }}</span>
      </div>
      <div v-if="isLost && mapping.lostAt" class="deal-card__detail">
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="deal-card__detail-label">
          {{ t('CRM.DEAL.LOST_AT') }}:
        </span>
        <span class="deal-card__detail-value">{{ mapping.lostAt }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div v-if="showActions && hasDealValue" class="deal-card__actions">
      <button
        v-if="isOpen"
        class="deal-card__action deal-card__action--won"
        :title="t('CRM.DEAL.MARK_WON')"
        @click="emit('markWon')"
      >
        <fluent-icon icon="checkmark" size="14" />
      </button>
      <button
        v-if="isOpen"
        class="deal-card__action deal-card__action--lost"
        :title="t('CRM.DEAL.MARK_LOST')"
        @click="emit('markLost')"
      >
        <fluent-icon icon="dismiss" size="14" />
      </button>
      <button
        v-if="!isOpen"
        class="deal-card__action deal-card__action--reopen"
        :title="t('CRM.DEAL.REOPEN')"
        @click="emit('reopen')"
      >
        <fluent-icon icon="arrow-reply" size="14" />
      </button>
      <button
        class="deal-card__action deal-card__action--edit"
        :title="t('CRM.DEAL.EDIT')"
        @click="emit('edit')"
      >
        <fluent-icon icon="edit" size="14" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.deal-card {
  @apply p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700;
}

.deal-card--compact {
  @apply p-2;
}

.deal-card__header {
  @apply flex items-center justify-between gap-2;
}

.deal-card__value {
  @apply flex items-center gap-2;
}

.deal-card__value-amount {
  @apply text-base font-semibold text-slate-800 dark:text-slate-200;
}

.deal-card__probability {
  @apply text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded;
}

.deal-card__details {
  @apply mt-2 space-y-1;
}

.deal-card__detail {
  @apply flex items-center gap-1 text-xs;
}

.deal-card__detail-label {
  @apply text-slate-500 dark:text-slate-400;
}

.deal-card__detail-value {
  @apply text-slate-700 dark:text-slate-300;
}

.deal-card__detail-value--weighted {
  @apply font-medium text-green-600 dark:text-green-400;
}

.deal-card__detail-value--overdue {
  @apply text-red-600 dark:text-red-400;
}

.deal-card__actions {
  @apply flex items-center gap-1 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700;
}

.deal-card__action {
  @apply p-1.5 rounded transition-colors;
}

.deal-card__action--won {
  @apply text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30;
}

.deal-card__action--lost {
  @apply text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30;
}

.deal-card__action--reopen {
  @apply text-woot-600 hover:bg-woot-100 dark:hover:bg-woot-900/30;
}

.deal-card__action--edit {
  @apply text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 ml-auto;
}
</style>
